import { Component, OnInit } from "@angular/core";
import { WishlistService } from "../../services/wishlistService/wishlist.service";
import { ProductAttributes } from "@models/product.model";
import { Router } from "@angular/router";
import { GenericDialogComponent } from "../generic-dialog/generic-dialog.component";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { MatDialog } from "@angular/material/dialog";
import { CartItemService } from "../../services/cart-item/cart-item.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { finalize } from "rxjs";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { TranslateService } from "@ngx-translate/core";
import { WishListAttributes } from "@models/wishlist.model";
import { BaseCart } from "@models/cart.model";
import { Location } from "@angular/common";
import { v4 as uuidv4 } from "uuid";

@Component({
    selector: "app-wishlist-page",
    templateUrl: "./wishlist-page.component.html",
    styleUrls: ["./wishlist-page.component.scss"],
    standalone: false,
})
export class WishlistPageComponent implements OnInit {
    public wishlistData: ProductAttributes[] = [];
    public visibleItems: ProductAttributes[] = [];
    public totalWishListCount: number = 0;
    public skipValue: number = 0;
    public isLoading: boolean = false;
    public initialPageSize: number = 8;
    public itemLoadingState: { [productId: number]: boolean } = {}; // Loading state by product(on removing and addToCart)

    constructor(
        private wishlistService: WishlistService,
        private router: Router,
        private dialog: MatDialog,
        private cartService: CartItemService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private location: Location,
    ) {}

    ngOnInit(): void {
        this.loadMyWishlistItems();
        this.wishlistService.wishlistSignal$.subscribe((productId) => {
            if (productId) {
                this.removeItemFromWishlist(productId);
            }
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private loadMyWishlistItems(): void {
        this.isLoading = true;
        this.wishlistService
            .getWishListItems(this.skipValue, this.initialPageSize)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                }),
            )
            .subscribe({
                next: (response) => {
                    this.totalWishListCount = response.count;
                    const existingIds = new Set(this.wishlistData.map((item) => item.id));
                    const newProducts = response.rows
                        .map((item: WishListAttributes) => item.product!)
                        .filter((product: ProductAttributes) => !existingIds.has(product.id));
                    this.wishlistData = [...this.wishlistData!, ...newProducts!];
                },
                error: (error) => {
                    throw error;
                },
            });
    }

    public loadMoreWishlistItems(): void {
        this.skipValue = this.wishlistData.length;
        this.loadMyWishlistItems();
    }

    public showViewMoreButton(): boolean {
        return this.wishlistData.length < this.totalWishListCount;
    }

    public onClickProduct(productId: number): void {
        this.router.navigate([`products/${productId}`]);
    }

    public deleteWishlistItem(item: ProductAttributes): void {
        this.openToremoveItemFromWishList(item);
    }

    // Open to Dialog for Remove item from Wishlist
    private openToremoveItemFromWishList(item: ProductAttributes) {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.RemoveFromWishlist,
                title: this.getTranslation("genericDialogBox.removeItemWishlist.title"),
                image: item.images?.imagesUrl[0],
                content: this.getTranslation("genericDialogBox.removeItemWishlist.content"),
                firstBtn: this.getTranslation("genericDialogBox.button.removeBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.removeItemFromWishlist(item.id);
            }
        });
    }

    //Remove from the Wishlist
    public removeItemFromWishlist(productId: number, isMoveFromCart: boolean = false): void {
        this.itemLoadingState[productId] = true; // disable cart and remove button
        this.wishlistService.deleteFromMyWishlist(productId).subscribe((response) => {
            if (response) {
                this.snackBar.open(
                    isMoveFromCart ? this.getTranslation("wishlist.message.movetToCart") : response,
                    SnackBarConfig.Actions.OKAY,
                    {
                        duration: SnackBarConfig.Duration.MEDIUM,
                    },
                );
                this.wishlistData = this.wishlistData.filter((items) => items.id !== productId);
                this.itemLoadingState[productId] = false; //// Enable cart and remove button
                this.totalWishListCount -= 1;
                if (this.wishlistData.length === 0 || this.totalWishListCount === 5) {
                    this.skipValue = 0;
                    this.loadMyWishlistItems();
                }
            }
        });
    }

    //Move to Cart
    public moveToCart(product: ProductAttributes, quantity: number): void {
        this.itemLoadingState[product.id] = true; // Disbale cart button
        this.cartService.getCartItems().subscribe((cartResponse) => {
            const cartItems = cartResponse;
            const existingItem = cartItems.find((item) => item.product?.id === product.id);
            if (existingItem && existingItem.product) {
                // Update quantity if the item is already in the cart
                this.updateCartQuantity(existingItem.product.id, existingItem.structureId!, ++existingItem.quantity!);
            } else {
                // Add new item to the cart if not exist
                this.addToCart(product, quantity);
            }
        });
    }

    private updateCartQuantity(productId: number, structureId: number, updatedQuantity: number): void {
        this.cartService.updateCartQuantity(productId, updatedQuantity, undefined, structureId).subscribe({
            next: (response) => {
                if (response) {
                    this.removeItemFromWishlist(productId, true);
                }
            },
        });
    }

    private addToCart(product: ProductAttributes, quantity: number): void {
        const body: BaseCart = { productID: product.id, quantity: quantity, cartUUID: uuidv4() };
        this.cartService.addToCart(body, product).subscribe({
            next: (response) => {
                if (response) {
                    this.removeItemFromWishlist(product.id, true);
                }
            },
        });
    }

    public goToHomePage(): void {
        this.router.navigate(["/home"]);
    }

    public getdiscountPercentage(product: ProductAttributes): number {
        return Math.round(((product.price - product?.discountedPrice!) / product.price) * 100);
    }

    public clickShopNow(): void {
        this.router.navigate(["home"]);
    }

    public goBack(): void {
        this.location.back();
    }

    public getStarsArray(): number[] {
        return Array.from({ length: 5 }, (_, i) => i);
    }

    public formatPrice(price: number): string {
        return price.toLocaleString();
    }

    public getFilledStars(product: ProductAttributes): number {
        return Math.floor(product.averageRating!);
    }
}
